import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTicketDropdowns } from '../../store/ticketSlice';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  Fade,
  Collapse,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ListItemIcon,
  Tooltip,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Add,
  Delete,
  ChecklistRtl,
  Flag,
  CheckCircle,
  RadioButtonUnchecked,
  Edit,
  Schedule,
  ExpandMore,
  MoreHoriz,
  PersonAdd,
  Close,
  ChevronRight
} from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { checklistStyles } from '../../shared/globalSX';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { useTranslation } from 'react-i18next';
import { CHECKLIST_CHANGE_TYPES } from '../../shared/appConstants';
import { getPriorityColor } from '../../shared/commonFunctions';
import { getEntityId, normalizeDateValue } from '../../shared/formNormalizationUtils';

const ChecklistManager = React.forwardRef(({ value = [], onChange, onClose, readOnly = false, projectId = null, taskId = null, showHeader = true, hideGlobalProgress = false, isCreateMode = false }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dropdowns } = useSelector(state => state.tickets);
  const dropdownsFetched = React.useRef(false);

  // Fetch dropdowns on mount if projectId is provided
  useEffect(() => {
    if (projectId && !dropdownsFetched.current && (!dropdowns.assignees || dropdowns.assignees.length === 0)) {
      dropdownsFetched.current = true;
      dispatch(fetchTicketDropdowns(projectId));
    }
  }, [projectId, dispatch, dropdowns.assignees]);

  // Get assignee options from API or use defaults
  const assigneeOptions = useMemo(() => {
    if (dropdowns.assignees && dropdowns.assignees.length > 0) {
      return dropdowns.assignees.map(a => ({ value: a.id, label: a.name }));
    }
    return [];
  }, [dropdowns.assignees]);

  // Get priority options from API or use defaults
  const priorityOptions = useMemo(() => {
    if (dropdowns.priorities && dropdowns.priorities.length > 0) {
      return dropdowns.priorities.map(p => ({ value: p.id, label: p.name }));
    }
    return [];
  }, [dropdowns.priorities]);

  const normalizeAssignee = useCallback((value) => {
    if (!value) return null;

    if (typeof value === 'object') {
      if (value.name) return { ...value };
      if (value.id) {
        const match = dropdowns.assignees?.find((a) => a.id === value.id);
        return match ? { id: match.id, name: match.name } : null;
      }
      return null;
    }

    if (typeof value === 'string') {
      const byId = dropdowns.assignees?.find((a) => a.id === value);
      if (byId) return { id: byId.id, name: byId.name };
      return { id: null, name: value };
    }

    return null;
  }, [dropdowns.assignees]);

  const normalizePriority = useCallback((value) => {
    if (!value) return null;

    if (typeof value === 'object') {
      if (value.name) return { ...value };
      if (value.id) {
        const match = dropdowns.priorities?.find((p) => p.id === value.id);
        return match ? { id: match.id, name: match.name } : null;
      }
      return null;
    }

    if (typeof value === 'string') {
      const byId = dropdowns.priorities?.find((p) => p.id === value);
      if (byId) return { id: byId.id, name: byId.name };
      return { id: null, name: value };
    }

    return null;
  }, [dropdowns.priorities]);
  const [newItemText, setNewItemText] = useState('');
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [newItemDueDate, setNewItemDueDate] = useState('');
  const [newItemPriority, setNewItemPriority] = useState('');
  const [newItemAssignee, setNewItemAssignee] = useState('');
  const [newChecklistDraftItems, setNewChecklistDraftItems] = useState([]);
  const [localValue, setLocalValue] = useState([]);
  const localValueRef = React.useRef([]);
  const pendingChangesByItemIdRef = React.useRef({});
  const [expandedChecklists, setExpandedChecklists] = useState(new Set());
  const [hoveredItem, setHoveredItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemData, setEditingItemData] = useState({});
  const [inlineItemDueDate, setInlineItemDueDate] = useState({});
  const [inlineItemPriority, setInlineItemPriority] = useState({});
  const [inlineItemAssignee, setInlineItemAssignee] = useState({});
  const [openInlineAddForms, setOpenInlineAddForms] = useState(new Set());
  const [openNewChecklistForm, setOpenNewChecklistForm] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dragOverChecklistTitle, setDragOverChecklistTitle] = useState(null);
  const [dragPosition, setDragPosition] = useState('after');

  const [checklistMenuAnchor, setChecklistMenuAnchor] = useState(null);
  const [activeChecklistTitle, setActiveChecklistTitle] = useState(null);
  const [itemMenuAnchor, setItemMenuAnchor] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  // Popover States
  const [lastDeletedItem, setLastDeletedItem] = useState(null);
  const [showUndoSnackbar, setShowUndoSnackbar] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [assigneeAnchor, setAssigneeAnchor] = useState(null);
  const [priorityAnchor, setPriorityAnchor] = useState(null);
  const [dueDateAnchor, setDueDateAnchor] = useState(null);

  const selectMenuProps = checklistStyles.selectMenuProps;
  const defaultChecklistTitle = t('taskModal.checklistManager.defaultChecklistTitle');
  const checklistFallbackPrefix = t('taskModal.checklistManager.checklistFallbackPrefix');

  const checklistInput = useMemo(() => {
    // Supports payload object shape: { checklists: [...] }
    if (value && !Array.isArray(value) && Array.isArray(value.checklists)) {
      return { checklistsArray: value.checklists, isNested: true };
    }

    if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0]?.checklists)) {
      // Supports array-wrapped payload shape: [{ checklists: [...] }]
      return { checklistsArray: value[0].checklists, isNested: true };
    }

    if (Array.isArray(value) && value.length > 0 && value[0]?.hasOwnProperty('title') && value[0]?.hasOwnProperty('items')) {
      // Supports direct nested checklist array shape
      return { checklistsArray: value, isNested: true };
    }

    if (Array.isArray(value)) {
      // Flat checklist items shape
      return { checklistsArray: value, isNested: false };
    }

    return { checklistsArray: [], isNested: false };
  }, [value]);

  // Check if value is in nested format or old flat format
  const isNestedFormat = useMemo(() => {
    // Force nested format for new/empty checklists to ensure API compatibility
    return checklistInput.isNested || (!checklistInput.checklistsArray || checklistInput.checklistsArray.length === 0);
  }, [checklistInput]);

  // Convert nested format to flat format for backward compatibility
  const flattenedValue = useMemo(() => {
    // Handle empty values
    if (!checklistInput.checklistsArray || checklistInput.checklistsArray.length === 0) {
      return [];
    }

    if (!isNestedFormat) {
      return checklistInput.checklistsArray.map((item, index) => ({
        ...item,
        id: item.id || `flat-${item.text ? item.text.replace(/\\s+/g, '-').slice(0, 30) : index}`,
        assignedTo: normalizeAssignee(item.assignedTo),
        priority: normalizePriority(item.priority)
      }));
    }

    const checklistsArray = checklistInput.checklistsArray;

    const flattened = [];
    checklistsArray.forEach((checklist, checklistIndex) => {
      if (checklist.items && Array.isArray(checklist.items)) {
        checklist.items.forEach((item, itemIndex) => {
          flattened.push({
            id: item.id || `item-${checklistIndex}-${item.text ? item.text.replace(/\\s+/g, '-').slice(0, 30) : itemIndex}`,
            text: item.text || '',
            completed: item.completed || false,
            checklistTitle: checklist.title || `${checklistFallbackPrefix} ${checklistIndex + 1}`,
            assignedTo: normalizeAssignee(item.assignedTo),
            dueDate: item.dueDate || null,
            priority: normalizePriority(item.priority),
            position: item.position || itemIndex + 1,
            checklistId: checklist.id || checklist.title || `cl-${checklistIndex}`
          });
        });
      }
    });
    return flattened;
  }, [checklistInput, isNestedFormat, normalizeAssignee, normalizePriority, checklistFallbackPrefix]);

  // Initialize local state and expanded checklists
  useEffect(() => {
    setLocalValue((prev) => {
      const currentList = localValueRef.current?.length ? localValueRef.current : (prev || []);
      const pendingChangesMap = pendingChangesByItemIdRef.current || {};

      const reconciled = (flattenedValue || []).map((serverItem) => {
        const itemId = String(serverItem.id);
        const pending = pendingChangesMap[itemId];
        if (!pending) return serverItem;

        const updatedPending = { ...pending };
        let hasPending = false;

        if ('assignedTo' in updatedPending) {
          const serverAssigneeId = String(getEntityId(serverItem.assignedTo) || '');
          const pendingAssigneeId = String(getEntityId(updatedPending.assignedTo) || '');
          if (serverAssigneeId === pendingAssigneeId) {
            delete updatedPending.assignedTo;
          } else {
            hasPending = true;
          }
        }

        if ('priority' in updatedPending) {
          const serverPriorityId = String(getEntityId(serverItem.priority) || '');
          const pendingPriorityId = String(getEntityId(updatedPending.priority) || '');
          if (serverPriorityId === pendingPriorityId) {
            delete updatedPending.priority;
          } else {
            hasPending = true;
          }
        }

        if ('dueDate' in updatedPending) {
          const serverDate = normalizeDateValue(serverItem.dueDate);
          const pendingDate = normalizeDateValue(updatedPending.dueDate);
          if (serverDate === pendingDate) {
            delete updatedPending.dueDate;
          } else {
            hasPending = true;
          }
        }

        if ('completed' in updatedPending) {
          if (Boolean(serverItem.completed) === Boolean(updatedPending.completed)) {
            delete updatedPending.completed;
          } else {
            hasPending = true;
          }
        }

        if ('text' in updatedPending) {
          if ((serverItem.text || '').trim() === (updatedPending.text || '').trim()) {
            delete updatedPending.text;
          } else {
            hasPending = true;
          }
        }

        if (hasPending) {
          pendingChangesMap[itemId] = updatedPending;
          return {
            ...serverItem,
            ...updatedPending
          };
        }

        delete pendingChangesMap[itemId];
        return serverItem;
      });

      const pendingItems = currentList.filter(
        (localItem) => !flattenedValue.some((serverItem) => (
          (serverItem.id && localItem.id && String(serverItem.id) === String(localItem.id)) ||
          (serverItem.text === localItem.text && serverItem.checklistTitle === localItem.checklistTitle)
        ))
      );

      const nextList = pendingItems.length > 0 ? [...reconciled, ...pendingItems] : reconciled;
      localValueRef.current = nextList;
      return nextList;
    });

    // Only initialize expansion for all groups if we haven't done it yet
    if (isNestedFormat && flattenedValue.length > 0 && expandedChecklists.size === 0) {
      const allTitles = new Set(flattenedValue.map(item => item.checklistTitle || defaultChecklistTitle));
      setExpandedChecklists(allTitles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flattenedValue, isNestedFormat, defaultChecklistTitle]);

  // Group items by checklist for nested display
  const groupedItems = useMemo(() => {
    if (!isNestedFormat) {
      // For flat format, group by checklistTitle to show as nested
      const grouped = {};
      localValue.forEach(item => {
        const title = item.checklistTitle || defaultChecklistTitle;
        if (!grouped[title]) {
          grouped[title] = [];
        }
        grouped[title].push(item);
      });
      return grouped;
    }

    const grouped = {};
    localValue.forEach(item => {
      const title = item.checklistTitle || defaultChecklistTitle;
      if (!grouped[title]) {
        grouped[title] = [];
      }
      grouped[title].push(item);
    });
    return grouped;
  }, [localValue, isNestedFormat, defaultChecklistTitle]);

  // Calculate progress for each checklist
  // eslint-disable-next-line no-unused-vars
  const getChecklistProgress = useCallback((items) => {
    const completedCount = items.filter(item => item.completed).length;
    const totalCount = items.length;
    return { completedCount, totalCount, progress: totalCount > 0 ? (completedCount / totalCount) * 100 : 0 };
  }, []);

  // Format date for display
  const formatDate = useCallback((dateString) => {
    if (!dateString) return null;
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      const month = d.toLocaleString('en-US', { month: 'short' });
      return `${d.getDate()} ${month} ${d.getFullYear().toString().slice(-2)}`;
    } catch {
      return dateString;
    }
  }, []);

  // Normalize any date value to HTML date input format (YYYY-MM-DD)
  const toDateInputValue = useCallback((dateString) => {
    if (!dateString) return '';
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }, []);


  // Get due date status
  const getDueDateStatus = useCallback((dateString) => {
    if (!dateString) return { status: 'none', color: COLORS.gray[400] };

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = date.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return { status: 'overdue', color: '#ff1744' };
      if (diffDays === 0) return { status: 'today', color: COLORS.ui.orangeWarning };
      if (diffDays <= 3) return { status: 'soon', color: COLORS.ui.orangeWarning };
      return { status: 'future', color: COLORS.ui.greenSuccess };
    } catch {
      return { status: 'none', color: COLORS.gray[400] };
    }
  }, []);

  // Convert flat format back to nested format with API payload
  const convertToNestedFormat = useCallback((flatItems) => {
    const checklists = {};

    flatItems.forEach(item => {
      const checklistTitle = item.checklistTitle || defaultChecklistTitle;
      const checklistId = item.checklistId || crypto.randomUUID();

      if (!checklists[checklistTitle]) {
        checklists[checklistTitle] = {
          id: checklistId,
          title: checklistTitle,
          position: Object.keys(checklists).length + 1,
          items: []
        };
      }

      const priorityValue = item.priority;
      const assigneeValue = item.assignedTo;

      // Resolve priority id from {id}, {name}, raw id, or raw name
      let priorityId = null;
      if (typeof priorityValue === 'object' && priorityValue?.id) {
        priorityId = priorityValue.id;
      } else if (typeof priorityValue === 'object' && priorityValue?.name) {
        priorityId = dropdowns.priorities?.find((p) => p.name === priorityValue.name)?.id || null;
      } else if (typeof priorityValue === 'string') {
        priorityId =
          dropdowns.priorities?.find((p) => p.id === priorityValue)?.id ||
          dropdowns.priorities?.find((p) => p.name === priorityValue)?.id ||
          null;
      }

      // Resolve assignee id from {id}, {name}, raw id, or raw name
      let assigneeId = null;
      if (typeof assigneeValue === 'object' && assigneeValue?.id) {
        assigneeId = assigneeValue.id;
      } else if (typeof assigneeValue === 'object' && assigneeValue?.name) {
        assigneeId = dropdowns.assignees?.find((a) => a.name === assigneeValue.name)?.id || null;
      } else if (typeof assigneeValue === 'string') {
        assigneeId =
          dropdowns.assignees?.find((a) => a.id === assigneeValue)?.id ||
          dropdowns.assignees?.find((a) => a.name === assigneeValue)?.id ||
          null;
      }

      checklists[checklistTitle].items.push({
        id: item.id,
        text: item.text,
        completed: item.completed,
        assignedTo: assigneeId || null,
        dueDate: item.dueDate ? new Date(item.dueDate).toISOString() : null,
        priority: priorityId || null,
        position: checklists[checklistTitle].items.length + 1
      });
    });

    const payload = {
      checklists: Object.values(checklists)
    };

    if (taskId) {
      payload.task_id = taskId;
    }

    return payload;
  }, [dropdowns.priorities, dropdowns.assignees, taskId, defaultChecklistTitle]);

  const emitChecklistChange = useCallback((updatedList, changeMeta = {}) => {
    localValueRef.current = updatedList;
    setLocalValue(updatedList);
    const convertedValue = isNestedFormat ? convertToNestedFormat(updatedList) : updatedList;
    try {
      onChange(convertedValue, changeMeta);
    } catch (err) {
      console.error('Checklist update failed', err);
    }
  }, [convertToNestedFormat, isNestedFormat, onChange]);

  const resetNewChecklistItemFields = () => {
    setNewItemText('');
    setNewItemDueDate('');
    setNewItemPriority('');
    setNewItemAssignee('');
  };

  const resetNewChecklistForm = () => {
    setOpenNewChecklistForm(false);
    setNewItemText('');
    setNewChecklistTitle('');
    setNewItemDueDate('');
    setNewItemPriority('');
    setNewItemAssignee('');
    setNewChecklistDraftItems([]);
  };

  const resolvePriority = useCallback((val) => {
    if (!val) return null;
    const idOrName = typeof val === 'object' ? (val.id || val.name) : val;
    return dropdowns.priorities?.find(p => p.id === idOrName || p.name === idOrName) || (typeof val === 'object' ? val : { id: val, name: val });
  }, [dropdowns.priorities]);

  const resolveAssignee = useCallback((val) => {
    if (!val) return null;
    const idOrName = typeof val === 'object' ? (val.id || val.name) : val;
    return dropdowns.assignees?.find(a => a.id === idOrName || a.name === idOrName) || (typeof val === 'object' ? val : { id: val, name: val });
  }, [dropdowns.assignees]);

  const buildNewChecklistItem = (text, overrides = {}) => ({
    id: crypto.randomUUID(),
    text,
    completed: false,
    checklistTitle: newChecklistTitle.trim(),
    dueDate: overrides.dueDate ?? newItemDueDate ?? null,
    priority: overrides.priority !== undefined ? overrides.priority : (resolvePriority(newItemPriority) || null),
    assignedTo: overrides.assignedTo !== undefined ? overrides.assignedTo : (resolveAssignee(newItemAssignee) || null)
  });

  const updateDraftItem = (id, changes) => {
    setNewChecklistDraftItems(prev => prev.map(item => (
      item.id === id ? { ...item, ...changes } : item
    )));
  };

  const addDraftItem = () => {
    const trimmedItem = newItemText.trim();

    if (!trimmedItem) {
      alert(t('taskModal.checklistManager.validationItemRequired'));
      return;
    }

    setNewChecklistDraftItems(prev => [...prev, buildNewChecklistItem(trimmedItem)]);
    resetNewChecklistItemFields();
  };

  const saveNewChecklist = () => {
    const trimmedTitle = newChecklistTitle.trim();
    const trimmedItem = newItemText.trim();

    if (!trimmedTitle) {
      alert(t('taskModal.checklistManager.validationChecklistTitleRequired'));
      return;
    }

    const draftItems = newChecklistDraftItems.filter(item => item.text?.trim()).map(item => ({
      ...item,
      text: item.text.trim()
    }));
    if (trimmedItem) {
      draftItems.push(buildNewChecklistItem(trimmedItem));
    }

    if (draftItems.length === 0) {
      alert(t('taskModal.checklistManager.validationItemRequired'));
      return;
    }

    const finalizedItems = draftItems.map((item) => ({
      ...item,
      checklistTitle: trimmedTitle,
      priority: resolvePriority(item.priority) || null,
      assignedTo: resolveAssignee(item.assignedTo) || null
    }));
    const currentList = localValueRef.current?.length ? localValueRef.current : (localValue || []);
    const newList = [...currentList, ...finalizedItems];
    localValueRef.current = newList;
    emitChecklistChange(newList, {
      type: CHECKLIST_CHANGE_TYPES.ADD_ITEM,
      checklistTitle: trimmedTitle,
      item: finalizedItems[finalizedItems.length - 1],
      items: finalizedItems
    });

    resetNewChecklistForm();
  };

  const toggleItem = (id) => {
    const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
    const previousItem = currentItems.find(item => String(item.id) === String(id));
    if (!previousItem) return;

    const nextCompleted = !previousItem.completed;
    const updatedItem = { ...previousItem, completed: nextCompleted };

    pendingChangesByItemIdRef.current[String(id)] = {
      ...(pendingChangesByItemIdRef.current[String(id)] || {}),
      completed: nextCompleted
    };

    const updated = currentItems.map(item =>
      String(item.id) === String(id) ? updatedItem : item
    );
    localValueRef.current = updated;

    emitChecklistChange(updated, {
      type: CHECKLIST_CHANGE_TYPES.UPDATE_ITEM,
      checklistTitle: previousItem?.checklistTitle,
      previousItem,
      item: updatedItem,
      changes: {
        completed: nextCompleted
      }
    });
  };

  const deleteItem = (id) => {
    delete pendingChangesByItemIdRef.current[String(id)];
    const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
    const itemToBackup = currentItems.find(item => String(item.id) === String(id));
    if (itemToBackup) {
      setLastDeletedItem(itemToBackup);
    }

    const updated = currentItems.filter(item => String(item.id) !== String(id));
    localValueRef.current = updated;
    try {
      emitChecklistChange(updated, {
        type: CHECKLIST_CHANGE_TYPES.DELETE_ITEM,
        checklistTitle: itemToBackup?.checklistTitle,
        item: itemToBackup
      });
      setShowUndoSnackbar(true);
    } catch (err) {
      console.error('Checklist update failed', err);
    }
  };

  const undoDeleteItem = () => {
    if (!lastDeletedItem) return;

    const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
    const newList = [...currentItems, lastDeletedItem];
    localValueRef.current = newList;
    try {
      emitChecklistChange(newList, {
        type: CHECKLIST_CHANGE_TYPES.ADD_ITEM,
        checklistTitle: lastDeletedItem?.checklistTitle,
        item: lastDeletedItem
      });
      setShowUndoSnackbar(false);
      setLastDeletedItem(null);
    } catch (err) {
      console.error('Checklist undo failed', err);
    }
  };

  const deleteChecklist = (title) => {
    const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
    const updated = currentItems.filter(item => item.checklistTitle !== title && (item.checklistTitle || defaultChecklistTitle) !== title);
    localValueRef.current = updated;
    emitChecklistChange(updated, {
      type: CHECKLIST_CHANGE_TYPES.DELETE_CHECKLIST,
      checklistTitle: title
    });
  };

  const toggleChecklistExpansion = (checklistTitle) => {
    const newExpanded = new Set(expandedChecklists);
    if (newExpanded.has(checklistTitle)) {
      newExpanded.delete(checklistTitle);
    } else {
      newExpanded.add(checklistTitle);
    }
    setExpandedChecklists(newExpanded);
  };

  const toggleInlineAddForm = (checklistTitle) => {
    const updated = new Set(openInlineAddForms);
    if (updated.has(checklistTitle)) {
      updated.delete(checklistTitle);
      // Clear inline add states when cancelling
      setInlineItemDueDate({ ...inlineItemDueDate, [checklistTitle]: '' });
      setInlineItemPriority({ ...inlineItemPriority, [checklistTitle]: '' });
      setInlineItemAssignee({ ...inlineItemAssignee, [checklistTitle]: '' });
      const textInput = document.getElementById(`inline-text-${checklistTitle}`);
      if (textInput) textInput.value = '';
    } else {
      updated.add(checklistTitle);
    }
    setOpenInlineAddForms(updated);
  };

  const clearDragState = useCallback(() => {
    setDraggedItemId(null);
    setDragOverItemId(null);
    setDragOverChecklistTitle(null);
    setDragPosition('after');
  }, []);

  const applyChecklistChange = useCallback((updatedList, changeMeta = {}) => {
    emitChecklistChange(updatedList, changeMeta);
  }, [emitChecklistChange]);

  const moveItemToPosition = useCallback((draggedId, targetId = null, targetChecklistTitle = null, position = 'after') => {
    if (!draggedId) return;

    const currentItems = [...(localValueRef.current?.length ? localValueRef.current : (localValue || []))];
    const draggedItem = currentItems.find((item) => String(item.id) === String(draggedId));
    if (!draggedItem) return;

    const targetItem = targetId ? currentItems.find((item) => item.id === targetId) : null;
    const sourceChecklistTitle = draggedItem.checklistTitle || defaultChecklistTitle;
    const destinationChecklistTitle = targetChecklistTitle || targetItem?.checklistTitle || sourceChecklistTitle;

    // No-op if dropped onto itself in same checklist.
    if (targetItem && targetItem.id === draggedItem.id && destinationChecklistTitle === sourceChecklistTitle) {
      return;
    }

    const remainingItems = currentItems.filter((item) => item.id !== draggedId);
    const checklistOrder = [...new Set(currentItems.map((item) => item.checklistTitle || defaultChecklistTitle))];
    if (!checklistOrder.includes(destinationChecklistTitle)) {
      checklistOrder.push(destinationChecklistTitle);
    }

    const groupedByChecklist = {};
    checklistOrder.forEach((title) => {
      groupedByChecklist[title] = [];
    });

    remainingItems.forEach((item) => {
      const title = item.checklistTitle || defaultChecklistTitle;
      if (!groupedByChecklist[title]) {
        groupedByChecklist[title] = [];
        checklistOrder.push(title);
      }
      groupedByChecklist[title].push(item);
    });

    const movedItem = { ...draggedItem, checklistTitle: destinationChecklistTitle };
    const destinationItems = [...(groupedByChecklist[destinationChecklistTitle] || [])];
    let insertIndex = destinationItems.length;

    if (targetItem) {
      const targetIndex = destinationItems.findIndex((item) => item.id === targetItem.id);
      if (targetIndex !== -1) {
        insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
      }
    }

    destinationItems.splice(insertIndex, 0, movedItem);
    groupedByChecklist[destinationChecklistTitle] = destinationItems;

    const updatedItems = [];
    checklistOrder.forEach((title) => {
      (groupedByChecklist[title] || []).forEach((item, index) => {
        updatedItems.push({
          ...item,
          checklistTitle: title,
          position: index + 1
        });
      });
    });

    applyChecklistChange(updatedItems, {
      type: CHECKLIST_CHANGE_TYPES.REORDER
    });
  }, [applyChecklistChange, localValue, defaultChecklistTitle]);

  const completedCount = (localValue || []).filter(item => item.completed).length;
  const totalCount = (localValue || []).length;
  // eslint-disable-next-line no-unused-vars
  const overallProgress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const renderChecklistItem = (item, isNested = false, checklistTitle = null, duplicateIndex = 0) => {
    const isEditing = editingItem === item.id;
    const editData = editingItemData[item.id] || {};
    const isDragging = draggedItemId === item.id;
    const isDragTarget = dragOverItemId === item.id;
    const targetChecklistTitle = checklistTitle || item.checklistTitle || defaultChecklistTitle;
    // eslint-disable-next-line no-unused-vars
    const dueDateStatus = item.dueDate ? getDueDateStatus(item.dueDate) : null;
    // eslint-disable-next-line no-unused-vars
    const isCompletedItem = Boolean(item.completed);

    // Use text combined with duplicate index as primary stable key because backend replaces item UUIDs on every save
    const stableKey = item.text ? `${checklistTitle}-${item.text}-${duplicateIndex}` : item.id;

    return (
      <Fade in key={stableKey} timeout={300}>
        <Card
          draggable={!readOnly && !isEditing}
          elevation={0}
          onMouseEnter={() => setHoveredItem(item.id)}
          onMouseLeave={() => {
            // Don't clear hovered state if the menu or any popover is open for this item
            if (!itemMenuAnchor && !assigneeAnchor && !priorityAnchor && !dueDateAnchor) {
              setHoveredItem(null);
            }
          }}
          onDragStart={(e) => {
            if (readOnly || isEditing) return;
            e.dataTransfer.effectAllowed = 'move';
            setDraggedItemId(item.id);
            setDragOverChecklistTitle(targetChecklistTitle);
          }}
          onDragOver={(e) => {
            if (readOnly || !draggedItemId || draggedItemId === item.id) return;
            e.preventDefault();
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            const isBefore = e.clientY < rect.top + rect.height / 2;
            setDragOverItemId(item.id);
            setDragOverChecklistTitle(targetChecklistTitle);
            setDragPosition(isBefore ? 'before' : 'after');
          }}
          onDrop={(e) => {
            if (readOnly || !draggedItemId) return;
            e.preventDefault();
            e.stopPropagation();
            moveItemToPosition(draggedItemId, item.id, targetChecklistTitle, dragPosition);
            clearDragState();
          }}
          onDragEnd={clearDragState}
          sx={checklistStyles.itemCard(isDragging, isDragTarget, dragPosition, readOnly, isEditing)}
        >
          <CardContent sx={checklistStyles.itemCardContent}>
            <Box sx={checklistStyles.itemRow}>
              {/* Custom Checkbox */}
              <Box sx={{ ...checklistStyles.checkboxWrap, mr: 1.5 }}>
                <IconButton
                  size="small"
                  onClick={() => toggleItem(item.id)}
                  disabled={readOnly}
                  sx={checklistStyles.checkboxButton(item.completed)}
                >
                  {item.completed ? (
                    <CheckCircle sx={checklistStyles.checkIcon} />
                  ) : (
                    <RadioButtonUnchecked sx={checklistStyles.uncheckIcon} />
                  )}
                </IconButton>
              </Box>

              {/* Content Table Column-like Row */}
              <Box sx={checklistStyles.colChecklist}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, bgcolor: '#F8F9FA', borderRadius: '8px', border: '1px solid #E0E0E0' }}>
                    <TextField
                      value={editData.text !== undefined ? editData.text : item.text}
                      onChange={(e) => setEditingItemData({ ...editingItemData, [item.id]: { ...editData, text: e.target.value } })}
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      autoFocus
                      placeholder={t('taskModal.checklistManager.addNewItemPlaceholder')}
                      sx={checklistStyles.editTextField}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        type="date"
                        size="small"
                        value={editData.dueDate !== undefined ? editData.dueDate : toDateInputValue(item.dueDate)}
                        onChange={(e) => setEditingItemData({ ...editingItemData, [item.id]: { ...editData, dueDate: e.target.value } })}
                        sx={{ width: 160, bgcolor: COLORS.white, borderRadius: 1, '& .MuiInputBase-input': { px: 1.25 } }}
                      />
                      <Select
                        size="small"
                        displayEmpty
                        value={editData.priority !== undefined ? editData.priority : (resolvePriority(item.priority)?.id || '')}
                        onChange={(e) => setEditingItemData({ ...editingItemData, [item.id]: { ...editData, priority: e.target.value } })}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 120, bgcolor: COLORS.white }}
                      >
                        <MenuItem value=""><em style={{ color: COLORS.ui.grey999, fontStyle: 'normal' }}>{t('taskModal.priority')}</em></MenuItem>
                        {priorityOptions.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                      </Select>
                      <Select
                        size="small"
                        displayEmpty
                        value={editData.assignedTo !== undefined ? editData.assignedTo : (resolveAssignee(item.assignedTo)?.id || '')}
                        onChange={(e) => setEditingItemData({ ...editingItemData, [item.id]: { ...editData, assignedTo: e.target.value } })}
                        MenuProps={selectMenuProps}
                        sx={{ minWidth: 140, bgcolor: COLORS.white }}
                      >
                        <MenuItem value=""><em style={{ color: COLORS.ui.grey999, fontStyle: 'normal' }}>{t('taskModal.assignee')}</em></MenuItem>
                        {assigneeOptions.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                      </Select>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          const targetId = item.id;
                          const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                          const previousItem = currentItems.find(i => String(i.id) === String(targetId)) || item;
                          const selectedPriority = editData.priority !== undefined ? resolvePriority(editData.priority) : resolvePriority(previousItem.priority);
                          const selectedAssignee = editData.assignedTo !== undefined ? resolveAssignee(editData.assignedTo) : resolveAssignee(previousItem.assignedTo);
                          const newText = editData.text !== undefined ? editData.text : previousItem.text;
                          const newDueDate = editData.dueDate !== undefined ? editData.dueDate : previousItem.dueDate;

                          const updatedItem = {
                            ...previousItem,
                            text: newText,
                            dueDate: newDueDate,
                            priority: selectedPriority || null,
                            assignedTo: selectedAssignee || null
                          };

                          pendingChangesByItemIdRef.current[String(targetId)] = {
                            ...(pendingChangesByItemIdRef.current[String(targetId)] || {}),
                            text: newText,
                            dueDate: newDueDate,
                            priority: selectedPriority || null,
                            assignedTo: selectedAssignee || null
                          };

                          const updated = currentItems.map(i =>
                            String(i.id) === String(targetId) ? updatedItem : i
                          );
                          localValueRef.current = updated;

                          applyChecklistChange(updated, {
                            type: CHECKLIST_CHANGE_TYPES.UPDATE_ITEM,
                            checklistTitle: previousItem?.checklistTitle,
                            previousItem,
                            item: updatedItem,
                            changes: {
                              text: newText,
                              dueDate: newDueDate,
                              priority: selectedPriority || null,
                              assignedTo: selectedAssignee || null
                            }
                          });
                          setEditingItem(null);
                          setEditingItemData({});
                        }}
                        sx={{ textTransform: 'none', bgcolor: COLORS.brand.primaryDark, '&:hover': { bgcolor: COLORS.ui.brandPrimaryHover }, borderRadius: '6px', px: 2 }}
                      >
                        {t('common.save')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => { setEditingItem(null); setEditingItemData({}); }}
                        sx={{ textTransform: 'none', borderRadius: '6px', px: 2 }}
                      >
                        {t('common.cancel')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <OverflowTooltip
                    title={item.text || ''}
                    tooltipWidth={520}
                    placement="top-start"
                    component={Typography}
                    maxLines={2}
                    variant="body1"
                    onClick={() => !readOnly && setEditingItem(item.id)}
                    sx={checklistStyles.itemTitle(item.completed, readOnly)}
                  />
                )}
              </Box>

              {/* Table Columns for Metadata or Hover Icons */}
              {!isEditing && (
                <>
                  {hoveredItem === item.id && !readOnly ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.5, pr: 1 }}>
                      <Tooltip
                        title={item.assignedTo?.name || t('taskModal.assignTo', 'Assignee')}
                        slotProps={{ tooltip: { className: 'app-tooltip' } }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                            const latestItem = currentItems.find(i => String(i.id) === String(item.id)) || item;
                            setActiveItem(latestItem);
                            setAssigneeAnchor(e.currentTarget);
                          }}
                        >
                          {item.assignedTo?.name ? (
                            <Avatar sx={checklistStyles.assigneeAvatar}>
                              {item.assignedTo.name?.charAt(0) || '?'}
                            </Avatar>
                          ) : (
                            <PersonAdd sx={{ fontSize: 18, color: COLORS.gray[500] }} />
                          )}
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        title={item.priority?.name || t('taskModal.priority', 'Priority')}
                        slotProps={{ tooltip: { className: 'app-tooltip' } }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                            const latestItem = currentItems.find(i => String(i.id) === String(item.id)) || item;
                            setActiveItem(latestItem);
                            setPriorityAnchor(e.currentTarget);
                          }}
                        >
                          <Flag sx={{ fontSize: 18, color: item.priority ? getPriorityColor(item.priority, { priorities: dropdowns.priorities }) : COLORS.gray[500] }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        title={item.dueDate ? formatDate(item.dueDate) : t('taskModal.dueDate', 'Due Date')}
                        slotProps={{ tooltip: { className: 'app-tooltip' } }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                            const latestItem = currentItems.find(i => String(i.id) === String(item.id)) || item;
                            setActiveItem(latestItem);
                            setDueDateAnchor(e.currentTarget);
                          }}
                          sx={{ gap: 0.5 }}
                        >
                          <Schedule sx={{ fontSize: 18, color: COLORS.gray[500] }} />
                          {item.dueDate && (
                            <Typography variant="caption" sx={{ color: COLORS.gray[600], fontSize: 11 }}>
                              {formatDate(item.dueDate)}
                            </Typography>
                          )}
                        </IconButton>
                      </Tooltip>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                          const latestItem = currentItems.find(i => String(i.id) === String(item.id)) || item;
                          setItemMenuAnchor(e.currentTarget);
                          setActiveItem(latestItem);
                        }}
                      >
                        <MoreHoriz sx={{ fontSize: 18, color: COLORS.gray[500] }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Box sx={checklistStyles.colAssignee}>
                        {item.assignedTo?.name ? (
                          <Avatar sx={checklistStyles.assigneeAvatar}>
                            {item.assignedTo.name?.charAt(0) || '?'}
                          </Avatar>
                        ) : '-'}
                      </Box>
                      <Box sx={checklistStyles.colPriority}>
                        {item.priority?.name ? (
                          <Flag sx={{ fontSize: 16, color: getPriorityColor(item.priority, { priorities: dropdowns.priorities }) }} />
                        ) : '-'}
                      </Box>
                      <Box sx={checklistStyles.colDueDate}>
                        <Typography variant="caption" sx={{ color: COLORS.gray[600] }}>
                          {item.dueDate ? formatDate(item.dueDate) : '-'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  const renderChecklistItemsOptimized = (items, isNested, checklistTitle) => {
    const textCounts = {};
    return items.map((item) => {
      const text = item.text || '';
      const duplicatesBefore = textCounts[text] || 0;
      textCounts[text] = duplicatesBefore + 1;
      return renderChecklistItem(item, isNested, checklistTitle, duplicatesBefore);
    });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddForm: () => setOpenNewChecklistForm(true),
    clearAllChecklists: () => {
      try {
        emitChecklistChange([], {
          type: CHECKLIST_CHANGE_TYPES.CLEAR_ALL
        });
        // Clear all new item states when clearing all checklists
        resetNewChecklistForm();
      } catch (err) {
        console.error('Checklist update failed', err);
      }
    }
  }));

  return (
    <Box sx={checklistStyles.container}>
      {/* Main Checklist Header — only shown when showHeader=true (e.g. in AddTaskModal) */}
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: COLORS.ui.border,
            border: `1px solid ${COLORS.ui.greyC4C4C4}`,
            borderRadius: '6px 6px 0 0',
            px: 1.5,
            py: 0.75,
            gap: 1
          }}
        >
          {/* X icon on left */}
          <IconButton size="small" sx={{ color: COLORS.gray[500], p: 0.5 }} onClick={onClose}>
            <Close sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Checklist icon */}
          <ChecklistRtl sx={{ color: COLORS.gray[600], fontSize: 20 }} />

          {/* Title */}
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: COLORS.gray[800], flexGrow: 1 }}>
            {t('taskModal.checklistManager.createChecklist', 'Create Checklist')}
          </Typography>

          {/* + button in a white box on the right */}
          {!readOnly && (
            <Box sx={{ bgcolor: COLORS.white, borderRadius: '4px', border: `1px solid ${COLORS.gray[200]}` }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setChecklistMenuAnchor(e.currentTarget);
                  setActiveChecklistTitle(Object.keys(groupedItems)[0] || null);
                }}
                sx={{ p: 0.5 }}
              >
                <Add sx={{ fontSize: 18, color: COLORS.gray[700] }} />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* Global Checklist Progress Bar */}
      {!hideGlobalProgress && totalCount > 0 && (
        <Box sx={{ mt: 2, mb: 2, px: 0.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" fontWeight="600" color="text.secondary">
              {t('taskModal.checklistManager.overallProgress', 'Overall Checklist Progress')}
            </Typography>
            <Typography variant="caption" fontWeight="600" sx={{ color: COLORS.primary }}>
              {completedCount}/{totalCount} ({Math.round(overallProgress)}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: COLORS.subtask.progressUnfilled,
              '& .MuiLinearProgress-bar': {
                bgcolor: overallProgress === 100 ? COLORS.success : COLORS.subtask.progressFilled,
                borderRadius: 4,
                transition: 'all 0.3s ease-in-out'
              }
            }}
          />
        </Box>
      )}

      {/* Add New Item */}
      {!readOnly && openNewChecklistForm && (
        <Paper elevation={0} sx={{ ...checklistStyles.footerAddPaper, mt: 0, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={checklistStyles.footerTitleLabel}>
                {t('taskModal.checklistManager.checklistTitleLabel')}
              </Typography>
              <TextField
                size="small"
                placeholder={t('taskModal.checklistManager.enterNewChecklistTitle')}
                value={newChecklistTitle}
                onChange={(e) => setNewChecklistTitle(e.target.value)}
                variant="outlined"
                fullWidth
                sx={checklistStyles.outlinedInputCommon}
              />
            </Box>
            <Button
              variant="contained"
              onClick={saveNewChecklist}
              disabled={!newChecklistTitle.trim() || (newChecklistDraftItems.length === 0 && !newItemText.trim())}
              sx={checklistStyles.footerAddButton}
            >
              {t('taskModal.checklistManager.saveChecklist', 'Save Checklist')}
            </Button>
            <Button
              variant="text"
              onClick={resetNewChecklistForm}
              sx={checklistStyles.cancelButton}
            >
              {t('common.cancel')}
            </Button>
          </Box>
          {newChecklistDraftItems.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
              {newChecklistDraftItems.map((item) => (
                <Box
                  key={item.id}
                  sx={checklistStyles.flexRowWrapBottom}
                >
                  <TextField
                    size="small"
                    placeholder={t('taskModal.addChecklistItem')}
                    value={item.text}
                    onChange={(e) => updateDraftItem(item.id, { text: e.target.value })}
                    variant="outlined"
                    sx={{ ...checklistStyles.inlineTextField, ...checklistStyles.outlinedInputCommon }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    value={item.dueDate || ''}
                    onChange={(e) => updateDraftItem(item.id, { dueDate: e.target.value })}
                    variant="outlined"
                    sx={{ ...checklistStyles.dateField, ...checklistStyles.outlinedInputCommon }}
                  />
                  <FormControl size="small" sx={checklistStyles.floatingSelectControl(140)}>
                    <InputLabel>{t('taskModal.priority')}</InputLabel>
                    <Select
                      value={resolvePriority(item.priority)?.id || ''}
                      onChange={(e) => updateDraftItem(item.id, { priority: resolvePriority(e.target.value) || null })}
                      label={t('taskModal.priority')}
                      MenuProps={selectMenuProps}
                      sx={checklistStyles.selectControl}
                    >
                      <MenuItem value="">{t('taskModal.checklistManager.none')}</MenuItem>
                      {priorityOptions.length > 0 ? (
                        priorityOptions.map((priority) => (
                          <MenuItem key={priority.value} value={priority.value}>{priority.label}</MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>{t('taskModal.checklistManager.noPrioritiesAvailable')}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={checklistStyles.floatingSelectControl(160)}>
                    <InputLabel>{t('taskModal.assignee')}</InputLabel>
                    <Select
                      value={resolveAssignee(item.assignedTo)?.id || ''}
                      onChange={(e) => updateDraftItem(item.id, { assignedTo: resolveAssignee(e.target.value) || null })}
                      label={t('taskModal.assignee')}
                      MenuProps={selectMenuProps}
                      sx={checklistStyles.selectControl}
                    >
                      <MenuItem value="">{t('taskModal.checklistManager.unassigned')}</MenuItem>
                      {assigneeOptions.length > 0 ? (
                        assigneeOptions.map((assignee) => (
                          <MenuItem key={assignee.value} value={assignee.value}>{assignee.label}</MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>{t('taskModal.checklistManager.noAssigneesAvailable')}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => setNewChecklistDraftItems(prev => prev.filter(draftItem => draftItem.id !== item.id))}
                    sx={{
                      width: 36,
                      height: 36,
                      border: `1px solid ${COLORS.gray[200]}`,
                      borderRadius: '6px',
                      color: COLORS.gray[500],
                      bgcolor: COLORS.white,
                      '&:hover': {
                        color: COLORS.error,
                        borderColor: COLORS.error,
                        bgcolor: COLORS.ui.error8
                      }
                    }}
                  >
                    <Close sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={checklistStyles.flexRowWrapBottom}>
            {/* Item Text Input */}
            <TextField
              size="small"
              placeholder={t('taskModal.addChecklistItem')}
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  addDraftItem();
                }
              }}
              variant="outlined"
              sx={{ ...checklistStyles.inlineTextField, ...checklistStyles.outlinedInputCommon }}
            />

            {/* Due Date Input */}
            <TextField
              size="small"
              type="date"
              value={newItemDueDate}
              onChange={(e) => setNewItemDueDate(e.target.value)}
              variant="outlined"
              sx={{ ...checklistStyles.dateField, ...checklistStyles.outlinedInputCommon }}
            />

            {/* Priority Dropdown */}
            <FormControl
              size="small"
              sx={checklistStyles.floatingSelectControl(140)}
            >
              <InputLabel>{t('taskModal.priority')}</InputLabel>
              <Select
                value={newItemPriority}
                onChange={(e) => setNewItemPriority(e.target.value)}
                label={t('taskModal.priority')}
                MenuProps={selectMenuProps}
                sx={checklistStyles.selectControl}
              >
                <MenuItem value="">{t('taskModal.checklistManager.none')}</MenuItem>
                {priorityOptions.length > 0 ? (
                  priorityOptions.map((priority) => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    {t('taskModal.checklistManager.noPrioritiesAvailable')}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Assignee Dropdown */}
            <FormControl
              size="small"
              sx={checklistStyles.floatingSelectControl(160)}
            >
              <InputLabel>{t('taskModal.assignee')}</InputLabel>
              <Select
                value={newItemAssignee}
                onChange={(e) => setNewItemAssignee(e.target.value)}
                label={t('taskModal.assignee')}
                MenuProps={selectMenuProps}
                sx={checklistStyles.selectControl}
              >
                <MenuItem value="">{t('taskModal.checklistManager.unassigned')}</MenuItem>
                {assigneeOptions.length > 0 ? (
                  assigneeOptions.map((assignee) => (
                    <MenuItem key={assignee.value} value={assignee.value}>
                      {assignee.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    {t('taskModal.checklistManager.noAssigneesAvailable')}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={addDraftItem}
              disabled={!newItemText.trim()}
              startIcon={<Add />}
              sx={checklistStyles.footerAddButton}
            >
              {t('taskModal.checklistManager.addItemButton')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Items Table — wrapped in a single border box */}
      <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '4px', overflow: 'hidden' }}>
        {Object.keys(groupedItems).length > 0 && (
          <Box sx={{
            ...checklistStyles.tableHeader,
            '& .MuiTypography-root': {
              fontSize: window.location.pathname.startsWith('/task/') ? '0.95rem' : '0.85rem',
              fontWeight: 800
            }
          }}>
            {/* Structural Indention Spacer to match the row's checkbox + gap */}
            <Box sx={{ width: 42 }} />
            <Box sx={checklistStyles.colChecklist}><Typography>{t('taskModal.checklistManager.colChecklist', 'Checklist')}</Typography></Box>
            <Box sx={checklistStyles.colAssignee}><Typography>{t('taskModal.checklistManager.colAssignee', 'Assignee')}</Typography></Box>
            <Box sx={checklistStyles.colPriority}><Typography>{t('taskModal.priority', 'Priority')}</Typography></Box>
            <Box sx={checklistStyles.colDueDate}><Typography>{t('taskModal.checklistManager.colDueDate', 'Due Date')}</Typography></Box>
          </Box>
        )}

        {/* Content — empty state or categorized sections */}
        {Object.keys(groupedItems).length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
            {Object.entries(groupedItems).map(([checklistTitle, items]) => {
              const completedCount = items.filter(i => i.completed).length;
              // eslint-disable-next-line no-unused-vars
              const progress = items.length > 0 ? (completedCount / items.length) * 100 : 0;
              const isExpanded = expandedChecklists.has(checklistTitle);

              return (
                <Box
                  key={checklistTitle}
                  sx={{
                    border: `1px solid ${COLORS.ui.greyEAEAEA}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    bgcolor: COLORS.white,
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: `0 2px 8px ${COLORS.ui.shadowLight}` }
                  }}
                >
                  {/* Section Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1.25,
                      bgcolor: COLORS.gray[50],
                      borderBottom: isExpanded ? `1px solid ${COLORS.ui.greyF0F0F0}` : 'none',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={() => toggleChecklistExpansion(checklistTitle)}
                  >
                    <IconButton size="small" sx={{ mr: 1, color: COLORS.gray[500] }}>
                      {isExpanded ? <ExpandMore sx={{ fontSize: 20 }} /> : <ChevronRight sx={{ fontSize: 20 }} />}
                    </IconButton>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        flex: 1,
                        color: COLORS.gray[800],
                        fontSize: '0.98rem'
                      }}
                    >
                      {checklistTitle}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {!readOnly && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isCreateMode) {
                                deleteChecklist(checklistTitle);
                              } else {
                                setSectionToDelete(checklistTitle);
                              }
                            }}
                            sx={{
                              color: COLORS.gray[400],
                              '&:hover': {
                                color: COLORS.error,
                                bgcolor: COLORS.ui.error8
                              }
                            }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleInlineAddForm(checklistTitle);
                            }}
                            sx={{ color: COLORS.gray[400], '&:hover': { color: COLORS.primary } }}
                          >
                            <Add sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Section Content */}
                  <Collapse in={isExpanded}>
                    <Box
                      sx={{ p: 0.5 }}
                      onDragOver={(e) => {
                        if (readOnly || !draggedItemId) return;
                        e.preventDefault();
                        setDragOverChecklistTitle(checklistTitle);
                        setDragOverItemId(null);
                        setDragPosition('after');
                      }}
                      onDrop={(e) => {
                        if (readOnly || !draggedItemId) return;
                        e.preventDefault();
                        e.stopPropagation();
                        moveItemToPosition(draggedItemId, null, checklistTitle, 'after');
                        clearDragState();
                      }}
                    >
                      {/* Inline Add Form inside Section */}
                      {!readOnly && openInlineAddForms.has(checklistTitle) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, m: 1, bgcolor: COLORS.gray[50], borderRadius: '8px', border: `1px solid ${COLORS.ui.greyE0E0E0}` }}>
                          <TextField
                            size="small"
                            autoFocus
                            placeholder={t('taskModal.checklistManager.addNewItemPlaceholder')}
                            variant="outlined"
                            inputRef={(el) => { if (el) el._checklistTitle = checklistTitle; }}
                            id={`inline-text-${checklistTitle}`}
                            sx={{ ...checklistStyles.inlineTextField, ...checklistStyles.outlinedInputCommon }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                const itemText = e.target.value.trim();
                                if (itemText) {
                                  const newItem = { id: crypto.randomUUID(), text: itemText, completed: false, checklistTitle, dueDate: inlineItemDueDate[checklistTitle] || null, priority: resolvePriority(inlineItemPriority[checklistTitle]) || null, assignedTo: resolveAssignee(inlineItemAssignee[checklistTitle]) || null };
                                  const currentList = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                                  const newList = [...currentList, newItem];
                                  localValueRef.current = newList;
                                  emitChecklistChange(newList, {
                                    type: CHECKLIST_CHANGE_TYPES.ADD_ITEM,
                                    checklistTitle,
                                    item: newItem
                                  });
                                  e.target.value = '';
                                  setInlineItemDueDate({ ...inlineItemDueDate, [checklistTitle]: '' });
                                  setInlineItemPriority({ ...inlineItemPriority, [checklistTitle]: '' });
                                  setInlineItemAssignee({ ...inlineItemAssignee, [checklistTitle]: '' });
                                }
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <TextField
                              size="small"
                              type="date"
                              variant="outlined"
                              value={inlineItemDueDate[checklistTitle] || ''}
                              onChange={(e) => setInlineItemDueDate({ ...inlineItemDueDate, [checklistTitle]: e.target.value })}
                              sx={{ width: 160, bgcolor: COLORS.white, borderRadius: 1, '& .MuiInputBase-input': { px: 1.25 } }}
                            />
                            <Select
                              size="small"
                              displayEmpty
                              value={inlineItemPriority[checklistTitle] || ''}
                              onChange={(e) => setInlineItemPriority({ ...inlineItemPriority, [checklistTitle]: e.target.value })}
                              MenuProps={selectMenuProps}
                              sx={{ minWidth: 120, bgcolor: COLORS.white }}
                            >
                              <MenuItem value=""><em style={{ color: COLORS.ui.grey999, fontStyle: 'normal' }}>{t('taskModal.priority')}</em></MenuItem>
                              {priorityOptions.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                            </Select>
                            <Select
                              size="small"
                              displayEmpty
                              value={inlineItemAssignee[checklistTitle] || ''}
                              onChange={(e) => setInlineItemAssignee({ ...inlineItemAssignee, [checklistTitle]: e.target.value })}
                              MenuProps={selectMenuProps}
                              sx={{ minWidth: 140, bgcolor: COLORS.white }}
                            >
                              <MenuItem value=""><em style={{ color: COLORS.ui.grey999, fontStyle: 'normal' }}>{t('taskModal.assignee')}</em></MenuItem>
                              {assigneeOptions.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                            </Select>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                const textInput = document.getElementById(`inline-text-${checklistTitle}`);
                                const itemText = textInput?.value?.trim();
                                if (itemText) {
                                  const newItem = { id: crypto.randomUUID(), text: itemText, completed: false, checklistTitle, dueDate: inlineItemDueDate[checklistTitle] || null, priority: resolvePriority(inlineItemPriority[checklistTitle]) || null, assignedTo: resolveAssignee(inlineItemAssignee[checklistTitle]) || null };
                                  const currentList = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                                  const newList = [...currentList, newItem];
                                  localValueRef.current = newList;
                                  emitChecklistChange(newList, {
                                    type: CHECKLIST_CHANGE_TYPES.ADD_ITEM,
                                    checklistTitle,
                                    item: newItem
                                  });
                                  if (textInput) textInput.value = '';
                                  setInlineItemDueDate({ ...inlineItemDueDate, [checklistTitle]: '' });
                                  setInlineItemPriority({ ...inlineItemPriority, [checklistTitle]: '' });
                                  setInlineItemAssignee({ ...inlineItemAssignee, [checklistTitle]: '' });
                                }
                              }}
                              sx={{ textTransform: 'none', bgcolor: COLORS.brand.primaryDark, '&:hover': { bgcolor: COLORS.ui.brandPrimaryHover }, borderRadius: '6px', px: 2 }}
                            >
                              {t('common.add')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => toggleInlineAddForm(checklistTitle)}
                              sx={{ textTransform: 'none', borderRadius: '6px', px: 2 }}
                            >
                              {t('common.cancel')}
                            </Button>
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {renderChecklistItemsOptimized(items, true, checklistTitle)}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ p: 1.5, textAlign: 'center', color: COLORS.gray[500], fontStyle: 'italic' }}>
            <Typography variant="caption" sx={{ fontSize: '0.875rem' }}>{t('taskModal.checklistManager.noChecklistAdded', 'No checklist added')}</Typography>
          </Box>
        )}

      </Box>
      {!readOnly && (
        <>
          {/* Checklist Loop Menu (dropdown on group header plus button) */}
          <Menu
            anchorEl={checklistMenuAnchor}
            open={Boolean(checklistMenuAnchor)}
            onClose={() => {
              setChecklistMenuAnchor(null);
              setActiveChecklistTitle(null);
              setHoveredItem(null);
            }}
          >
            <MenuItem onClick={() => {
              setOpenNewChecklistForm(true);
              setChecklistMenuAnchor(null);
            }}>
              <ListItemIcon><Add fontSize="small" /></ListItemIcon>
              <Typography variant="body2">{t('taskModal.checklistManager.addChecklist', 'Add Checklist')}</Typography>
            </MenuItem>

            <MenuItem
              onClick={() => {
                if (isCreateMode) {
                  if (activeChecklistTitle) {
                    deleteChecklist(activeChecklistTitle);
                  }
                } else {
                  if (window.confirm(t('common.confirmDelete'))) {
                    deleteChecklist(activeChecklistTitle);
                  }
                }
                setChecklistMenuAnchor(null);
              }}
              sx={{ color: COLORS.error }}
              disabled={isCreateMode && Object.keys(groupedItems).length === 0}
            >
              <ListItemIcon><Delete fontSize="small" sx={{ color: COLORS.error }} /></ListItemIcon>
              <Typography variant="body2">{t('taskModal.checklistManager.deleteChecklist', 'Delete Checklist')}</Typography>
            </MenuItem>
          </Menu>

          {/* Assignee Popover */}
          <Popover
            open={Boolean(assigneeAnchor)}
            anchorEl={assigneeAnchor}
            onClose={() => { setAssigneeAnchor(null); setHoveredItem(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: { p: 1, minWidth: 200 } }}
          >
            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>{t('taskModal.assignTo')}</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={
                  (() => {
                    const current = activeItem ? ((localValueRef.current?.length ? localValueRef.current : localValue).find(i => String(i.id) === String(activeItem.id)) || activeItem) : null;
                    return current?.assignedTo?.id || current?.assignedTo || '';
                  })()
                }
                onChange={(e) => {
                  const val = e.target.value;
                  const targetId = activeItem?.id;
                  if (!targetId) return;

                  const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                  const currentItem = currentItems.find(i => String(i.id) === String(targetId)) || activeItem;
                  const selected = dropdowns.assignees?.find(a => a.id === val) || (val ? { id: val, name: val } : null);
                  const updatedItem = { ...currentItem, assignedTo: selected };

                  pendingChangesByItemIdRef.current[String(targetId)] = {
                    ...(pendingChangesByItemIdRef.current[String(targetId)] || {}),
                    assignedTo: selected
                  };

                  const updated = currentItems.map(i => String(i.id) === String(targetId) ? updatedItem : i);
                  localValueRef.current = updated;

                  applyChecklistChange(updated, {
                    type: CHECKLIST_CHANGE_TYPES.UPDATE_ITEM,
                    checklistTitle: currentItem?.checklistTitle,
                    previousItem: currentItem,
                    item: updatedItem,
                    changes: {
                      assignedTo: selected
                    }
                  });
                  setAssigneeAnchor(null);
                  setHoveredItem(null);
                }}
                MenuProps={selectMenuProps}
              >
                <MenuItem value="">{t('taskModal.checklistManager.unassigned')}</MenuItem>
                {assigneeOptions.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Popover>

          {/* Priority Popover */}
          <Popover
            open={Boolean(priorityAnchor)}
            anchorEl={priorityAnchor}
            onClose={() => { setPriorityAnchor(null); setHoveredItem(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: { p: 1, minWidth: 150 } }}
          >
            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>{t('taskModal.priority')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {priorityOptions.map(p => (
                <MenuItem
                  key={p.value}
                  onClick={() => {
                    const targetId = activeItem?.id;
                    if (!targetId) return;

                    const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                    const currentItem = currentItems.find(i => String(i.id) === String(targetId)) || activeItem;
                    const selected = dropdowns.priorities?.find(pr => pr.id === p.value) || (p.value ? { id: p.value, name: p.label } : null);
                    const updatedItem = { ...currentItem, priority: selected };

                    pendingChangesByItemIdRef.current[String(targetId)] = {
                      ...(pendingChangesByItemIdRef.current[String(targetId)] || {}),
                      priority: selected
                    };

                    const updated = currentItems.map(i => String(i.id) === String(targetId) ? updatedItem : i);
                    localValueRef.current = updated;

                    applyChecklistChange(updated, {
                      type: CHECKLIST_CHANGE_TYPES.UPDATE_ITEM,
                      checklistTitle: currentItem?.checklistTitle,
                      previousItem: currentItem,
                      item: updatedItem,
                      changes: {
                        priority: selected
                      }
                    });
                    setPriorityAnchor(null);
                    setHoveredItem(null);
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon><Flag sx={{ fontSize: 16, color: getPriorityColor(p.label, { priorities: dropdowns.priorities }) }} /></ListItemIcon>
                  <Typography variant="body2">{p.label}</Typography>
                </MenuItem>
              ))}
            </Box>
          </Popover>

          {/* Due Date Popover */}
          <Popover
            open={Boolean(dueDateAnchor)}
            anchorEl={dueDateAnchor}
            onClose={() => { setDueDateAnchor(null); setHoveredItem(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: { p: 1.5, minWidth: 220 } }}
          >
            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>{t('taskModal.dueDate')}</Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={
                (() => {
                  const current = activeItem ? ((localValueRef.current?.length ? localValueRef.current : localValue).find(i => String(i.id) === String(activeItem.id)) || activeItem) : null;
                  return current?.dueDate ? toDateInputValue(current.dueDate) : '';
                })()
              }
              onChange={(e) => {
                const val = e.target.value;
                const targetId = activeItem?.id;
                if (!targetId) return;

                const currentItems = localValueRef.current?.length ? localValueRef.current : (localValue || []);
                const currentItem = currentItems.find(i => String(i.id) === String(targetId)) || activeItem;
                const updatedItem = { ...currentItem, dueDate: val || null };

                pendingChangesByItemIdRef.current[String(targetId)] = {
                  ...(pendingChangesByItemIdRef.current[String(targetId)] || {}),
                  dueDate: val || null
                };

                const updated = currentItems.map(i => String(i.id) === String(targetId) ? updatedItem : i);
                localValueRef.current = updated;

                applyChecklistChange(updated, {
                  type: CHECKLIST_CHANGE_TYPES.UPDATE_ITEM,
                  checklistTitle: currentItem?.checklistTitle,
                  previousItem: currentItem,
                  item: updatedItem,
                  changes: {
                    dueDate: val || null
                  }
                });
                setDueDateAnchor(null);
                setHoveredItem(null);
              }}
              sx={checklistStyles.outlinedInputCommon}
            />
          </Popover>

          {/* Item Context Menu */}
          <Menu
            anchorEl={itemMenuAnchor}
            open={Boolean(itemMenuAnchor)}
            onClose={() => {
              setItemMenuAnchor(null);
              setActiveItem(null);
              setHoveredItem(null);
            }}
          >
            <MenuItem onClick={() => {
              toggleInlineAddForm(activeItem.checklistTitle);
              setItemMenuAnchor(null);
            }}>
              <ListItemIcon><Add fontSize="small" /></ListItemIcon>
              <Typography variant="body2">{t('taskModal.checklistManager.addItemShort', 'Add Item')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => {
              setEditingItem(activeItem.id);
              setEditingItemData({
                [activeItem.id]: {
                  text: activeItem.text,
                  dueDate: toDateInputValue(activeItem.dueDate),
                  priority: resolvePriority(activeItem.priority)?.id || '',
                  assignedTo: resolveAssignee(activeItem.assignedTo)?.id || ''
                }
              });
              setItemMenuAnchor(null);
            }}>
              <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
              <Typography variant="body2">{t('common.edit', 'Edit Text')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => {
              deleteItem(activeItem.id);
              setItemMenuAnchor(null);
            }} sx={{ color: COLORS.error }}>
              <ListItemIcon><Delete fontSize="small" sx={{ color: COLORS.error }} /></ListItemIcon>
              <Typography variant="body2">{t('common.delete', 'Delete')}</Typography>
            </MenuItem>
          </Menu>
        </>
      )}

      {/* Section Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(sectionToDelete)}
        onClose={() => setSectionToDelete(null)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 1,
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, px: 2 }}>
          {t('taskModal.checklistManager.deleteChecklistTitle', 'Delete Checklist')}
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.gray[600] }}>
            {t('taskModal.checklistManager.deleteChecklistPrompt', 'Are you sure you want to delete the entire checklist "{{title}}"? This will permanently remove all items in this group.', { title: sectionToDelete })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setSectionToDelete(null)}
            sx={{
              color: COLORS.gray[600],
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (sectionToDelete) {
                deleteChecklist(sectionToDelete);
                setSectionToDelete(null);
              }
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '6px',
              px: 3,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undo Delete Snackbar */}
      <Snackbar
        open={showUndoSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowUndoSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={undoDeleteItem}>
              {t('common.undo', 'UNDO')}
            </Button>
          }
          sx={{
            width: '100%',
            bgcolor: COLORS.ui.snackbarBg,
            color: COLORS.white,
            '& .MuiAlert-icon': { color: COLORS.primary },
            borderRadius: '8px',
            boxShadow: COLORS.ui.shadowHeavy
          }}
        >
          {t('taskModal.checklistManager.itemDeleted', 'Item deleted')}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ChecklistManager;
